import React from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReplyIcon from '@mui/icons-material/Reply';

import { DialogCloseButton, SlideTransition } from '@components';
import { useCloseOnBack } from '@hooks';
import { countBadgeSx } from 'theme/badges';
import { buttonSizeSx, secondaryButtonSx } from 'theme/buttons';
import { touchTargetSx } from 'theme/touchTarget';

export const LIST_FILTERS_DIALOG = 'candidateFilters';

interface IListFiltersDialogProps {
  title: string;
  activeFilterCount: number;
  isDefault: boolean;
  search: React.ReactNode;
  controls?: React.ReactNode;
  children: React.ReactNode;
  onReset: () => void;
}

export const ListFiltersDialog: React.FC<IListFiltersDialogProps> = ({
  title,
  activeFilterCount,
  isDefault,
  search,
  controls,
  children,
  onReset,
}) => {
  const { open, openOnTop, close } = useCloseOnBack(LIST_FILTERS_DIALOG);

  return (
    <>
      <IconButton
        aria-label={
          activeFilterCount ? `Filters, ${activeFilterCount} active` : 'Filters'
        }
        onClick={openOnTop}
        sx={{
          display: { tablet: 'none' },
          ml: 'auto',
          color: 'text.secondary',
          ...touchTargetSx,
        }}
      >
        <Badge
          badgeContent={activeFilterCount}
          sx={{ '& .MuiBadge-badge': countBadgeSx }}
        >
          <FilterListIcon />
        </Badge>
      </IconButton>

      <Dialog
        fullScreen
        open={open}
        onClose={close}
        TransitionComponent={SlideTransition}
      >
        <Stack direction="row" alignItems="center" gap={1.5} p={2}>
          <DialogTitle variant="h3" sx={{ p: 0, flex: 1 }}>
            {title}
          </DialogTitle>
          <DialogCloseButton onClick={close} />
        </Stack>

        <DialogContent sx={{ px: 2, py: 0, overscrollBehavior: 'contain' }}>
          <Stack gap={2}>
            {search}

            {controls && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
                sx={{
                  '& .MuiSelect-select': { fontSize: '0.875rem' },
                  '& .MuiFormControlLabel-root': { mr: 0 },
                }}
              >
                {controls}
              </Stack>
            )}

            {children}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<ReplyIcon />}
            disabled={isDefault}
            onClick={onReset}
            sx={{ ...buttonSizeSx.md, ...secondaryButtonSx }}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
